import { useEffect, useRef, useState } from 'react'
import { data, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '../../../../components/button'
import { Input } from '../../../../components/input'
import { paths } from '../../../../router/paths'
import type { RootState } from '../../../../redux/global-store'
import { isJourneyActive } from '../../../../utils/journey-utils'
import {
  validateGuatemalaMobile,
  formatGuatemalaPhone,
  extractGuatemalaLocalDigits,
} from '../../../../utils/validators/guatemala-phone'
import {
  formatQuetzal,
  unformatQuetzal,
  quetzalToNumber,
} from '../../../../utils/masks/formatQuetzal'
import { formatLastFourDigits } from '../../../../utils/masks/formatLastFourDigits'
import { validateLastFourDigits } from '../../../../utils/validators/last-four-digits'
import { showToast } from '../../../../components/sonner'
import { PageContainer } from '../../../../components/page-container/page-container'
import {
  createCollectionOrderRequest,
  createCollectionOrderReset,
} from '../../presentation/redux/actions/create-collection-order-actions'
import { Loader } from '../../../../components/loader'
import { Close, InfoOutlined } from '@mui/icons-material'
import { styles, texts } from './new-sale.styles'

export const NewSale = () => {
  function handleAmountChange(value: string) {
    const formatted = formatQuetzal(value)
    setAmount(formatted)
  }

  function handlePhoneChange(value: string) {
    const localDigits = extractGuatemalaLocalDigits(value)
    if (!localDigits) {
      setPhone('')
      return
    }
    setPhone(formatGuatemalaPhone(localDigits))
  }

  function handleLastFourChange(value: string) {
    setLastFourOriginAccount(formatLastFourDigits(value))
  }

  function toggleAccountHint() {
    setShowAccountHint(prev => !prev)
  }

  function handleAccountHintOutsideClick(e: PointerEvent) {
    if (!accountFieldRef.current?.contains(e.target as Node)) {
      setShowAccountHint(false)
    }
  }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [amount, setAmount] = useState('')
  const [phone, setPhone] = useState('')
  const [lastFourOriginAccount, setLastFourOriginAccount] = useState('')
  const [showAccountHint, setShowAccountHint] = useState(false)
  const accountFieldRef = useRef<HTMLDivElement>(null)

  const createCollectionOrder = useSelector(
    (state: RootState) => state.createCollectionOrder.createCollectionOrder,
  )
  const user = useSelector((state: RootState) => state.user.getUser.user)
  const { journey } = useSelector((state: RootState) => state.journey.journey)

  const amountNumber = quetzalToNumber(amount)
  const phoneValidation = validateGuatemalaMobile(phone, {
    requireCountryCode: true,
  })
  const phoneValid = phoneValidation.valid
  const amountValid = !isNaN(amountNumber) && amountNumber > 0
  const lastFourValid = validateLastFourDigits(lastFourOriginAccount)
  const isValid = phoneValid && amountValid && lastFourValid

  // Fecha o aviso dos 4 dígitos ao clicar fora do campo
  useEffect(() => {
    if (!showAccountHint) return
    document.addEventListener('pointerdown', handleAccountHintOutsideClick)
    return () =>
      document.removeEventListener('pointerdown', handleAccountHintOutsideClick)
  }, [showAccountHint])

  useEffect(() => {
    if (createCollectionOrder?.data) {
      showToast('Token generado exitosamente', 'success')
      navigate(paths.collectionOrder.tokenSent)
    }
  }, [createCollectionOrder?.data, navigate])

  useEffect(() => {
    if (createCollectionOrder?.error) {
      dispatch(createCollectionOrderReset())
      showToast(createCollectionOrder?.error, 'error')
    }
  }, [createCollectionOrder?.error])

  // Validar se há jornada ativa antes de permitir vendas
  useEffect(() => {
    if (!isJourneyActive(journey)) {
      showToast('No hay jornada activa. Por favor inicia una jornada primero.', 'error')
      navigate(paths.journey.input)
    }
  }, [journey, navigate])

  function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault()
    if (!isValid || createCollectionOrder?.load) return

    const payload = {
      principalValue: amountNumber,
      payerPhone: "+" + phone.replace(/\D/g, ''),
      ...(lastFourOriginAccount && { lastFourOriginAccount }),
    }
    dispatch(createCollectionOrderRequest(payload))
  }

  return (
    <>
      <PageContainer type="authenticated" title="Nueva venta">
        {createCollectionOrder?.load && <Loader />}
        <form
          className="flex flex-1 flex-col justify-between px-4 py-6"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div className="flex flex-1 flex-col justify-start space-y-6 pt-6">
            <div className="flex flex-col gap-14">
              <Input
                value={amount}
                onChange={e => handleAmountChange(e.target.value)}
                placeholder="Monto"
                name="amount"
                inputMode="numeric"
                error={
                  !amountValid && amount !== '' ? texts.amountInvalid : undefined
                }
              />
              <Input
                value={phone}
                onChange={e => handlePhoneChange(e.target.value)}
                placeholder="Celular cliente"
                name="phone"
                maxLength={15}
              />
              <div className={styles.fieldWrapper} ref={accountFieldRef}>
                <Input
                  value={lastFourOriginAccount}
                  onChange={e => handleLastFourChange(e.target.value)}
                  placeholder={texts.lastFourPlaceholder}
                  name="lastFourOriginAccount"
                  inputMode="numeric"
                  maxLength={4}
                  rightIcon={
                    <InfoOutlined
                      fontSize="small"
                      aria-label={texts.lastFourHint}
                    />
                  }
                  onRightIconClick={toggleAccountHint}
                  error={!lastFourValid ? texts.lastFourInvalid : undefined}
                />
                {showAccountHint && (
                  <div className={styles.hint} role="note">
                    <span>{texts.lastFourHint}</span>
                    <button
                      type="button"
                      className={styles.hintClose}
                      aria-label={texts.closeHint}
                      onClick={toggleAccountHint}
                    >
                      <Close fontSize="small" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-6">
            <Button
              type="submit"
              disabled={!isValid || createCollectionOrder?.load}
              className="px-4 py-2 font-semibold cursor-pointer focus:outline-none transition-colors duration-150 bg-primary-pure text-white hover:bg-stone-800 border border-primary-pure disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed h-[55px] w-full rounded-[10px] text-xl font-semibold"
            >
              {createCollectionOrder?.load ? 'Envío...' : 'Generar Token'}
            </Button>
            <Button
              type="button"
              className="px-4 py-2 font-semibold cursor-pointer focus:outline-none transition-colors duration-150 bg-primary-pure text-white hover:bg-stone-800 border border-primary-pure disabled:bg-gray-300 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed h-[55px] w-full rounded-[10px] text-xl font-semibold"
              onClick={() => navigate(paths.home)}
            >
              Regresar
            </Button>
            {createCollectionOrder?.error && (
              <span className="text-sm text-red-500" role="alert">
                {createCollectionOrder?.error}
              </span>
            )}
          </div>
        </form>
      </PageContainer>
    </>
  )
}
